<template>
  <div class="supervisor-submit-tab timesheet-tab-shell">
    <div class="submit-toolbar timesheet-tab-toolbar">
      <div class="timesheet-toolbar-top">
        <div>
          <h2 class="timesheet-tab-title">My Submissions</h2>
          <p class="timesheet-tab-subtitle">
            Submit weekly timesheets and review your submission history
          </p>
        </div>
        <div class="timesheet-toolbar-actions">
          <div class="view-switch">
            <b-button
              :variant="view === 'form' ? 'primary' : 'outline-secondary'"
              class="view-switch-btn"
              @click="view = 'form'"
              >Submit Timesheet</b-button
            >
            <b-button
              :variant="view === 'history' ? 'primary' : 'outline-secondary'"
              class="view-switch-btn"
              @click="view = 'history'"
              >History</b-button
            >
          </div>
        </div>
      </div>

      <div class="timesheet-toolbar-panel submit-toolbar-panel">
        <div class="filter-field submit-week-field">
          <label class="filter-label">Week Starts On</label>
          <div class="week-start-display">{{ startDayName }}</div>
          <div class="week-start-hint">{{ weekStartHint }}</div>
        </div>
      </div>
    </div>

    <div class="submit-content timesheet-tab-content">
      <div class="submit-card">
        <div class="submit-card-header">
          <div>
            <h3 class="submit-card-title">
              {{ view === 'form' ? 'Submit Timesheet' : 'Submission History' }}
            </h3>
            <p class="submit-card-subtitle">
              {{
                view === 'form'
                  ? 'Add attendance for the selected week and submit it for review.'
                  : 'Review past submissions or amend one that needs changes.'
              }}
            </p>
          </div>
        </div>
        <supervisor-submit-form
          v-if="view === 'form'"
          :key="formKey"
          :amend-sheet="amendSheet"
          :start-day="companyStartDay"
          @project-changed="onProjectChanged"
          @start-day="onStartDay"
          @submitted="onSubmitted"
        />
        <supervisor-submit-history v-else :key="historyKey" @amend="onAmend" />
      </div>
    </div>
  </div>
</template>

<script>
import SupervisorSubmitForm from './SupervisorSubmitForm'
import SupervisorSubmitHistory from './SupervisorSubmitHistory'
import { settingsStartDay } from '@/utils/weekHelpers'

export default {
  name: 'SupervisorSubmitTab',
  components: { SupervisorSubmitForm, SupervisorSubmitHistory },
  data() {
    return {
      view: this.$route.query.timesheetId ? 'history' : 'form',
      formKey: 0,
      historyKey: 0,
      amendSheet: null,
      // What the "Week Starts On" display shows: the company default until a
      // project is picked in the form, then that project's pay-period start
      // day (reported by the form once its sheet loads).
      startDay: 1,
      companyStartDay: 1,
      activeProjectId: null,
    }
  },
  computed: {
    startDayName() {
      const names = [
        'Sunday',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
      ]
      return names[this.startDay] || 'Monday'
    },
    weekStartHint() {
      if (this.activeProjectId) {
        const rows = this.$store.getters['timesheet/projectPayPeriods'] || []
        const row = rows.find((p) => p.project_id === this.activeProjectId)
        if (row && row.has_override) return 'Set for this project'
        // Supervisors can't read the pay-period list above; fall back to the
        // day the form reported — a difference means a project override.
        if (this.startDay !== this.companyStartDay)
          return 'Set for this project'
      }
      return 'Set by your company'
    },
  },
  created() {
    // The week boundary is a company setting — clear any stale personal
    // override and always follow the server's configured start day.
    localStorage.removeItem('tp_week_start_day')
    // Only feeds the "Set for this project" hint; the displayed start day
    // itself comes from the form's loaded sheet.
    this.$store.dispatch('timesheet/getProjectPayPeriods')
    this.$store.dispatch('timesheet/getTimesheetSettings').then((settings) => {
      if (!settings) return
      this.companyStartDay = settingsStartDay(settings)
      if (!this.activeProjectId) this.startDay = this.companyStartDay
    })
  },
  methods: {
    onSubmitted() {
      // Next form open starts fresh — drop the project-scoped start day.
      this.activeProjectId = null
      this.startDay = this.companyStartDay
      this.historyKey++
      this.view = 'history'
    },
    onAmend(sheet) {
      this.amendSheet = sheet
      this.activeProjectId = (sheet && sheet.project_id) || null
      // The form re-emits `start-day` from the amended sheet's week_start.
      this.formKey++
      this.view = 'form'
    },
    // The form reports which project is selected (for the hint) and, once its
    // sheet loads, that sheet's pay-period start day.
    onProjectChanged(projectId) {
      this.activeProjectId = projectId || null
      if (!this.activeProjectId) this.startDay = this.companyStartDay
    },
    onStartDay(dow) {
      if (typeof dow === 'number') this.startDay = dow
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/layouts/timesheet-shared';

.submit-toolbar-panel {
  align-items: flex-end;
}

.submit-week-field {
  flex: 0 1 220px;
}

.view-switch {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;

  /deep/ .btn {
    min-height: 42px;
    min-width: 128px;
    padding: 0.5rem 1rem;
    font-size: 13px;
    font-weight: 600;
    border-radius: 10px !important;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
    transition: background-color 0.18s ease, border-color 0.18s ease,
      box-shadow 0.18s ease, transform 0.18s ease;
  }

  /deep/ .btn:hover,
  /deep/ .btn:focus {
    box-shadow: 0 4px 10px rgba(15, 23, 42, 0.12);
    transform: translateY(-1px);
  }

  /deep/ .btn-outline-secondary {
    background: #fff;
    border-color: #b8c2cc;
    color: #5f6b7a;
  }

  /deep/ .btn-outline-secondary:hover,
  /deep/ .btn-outline-secondary:focus {
    background: #f8fafc;
    border-color: #7b8794;
    color: #1f2d3d;
  }

  /deep/ .btn-primary {
    border-color: #0872d9;
    background: #0872d9;
  }
}

/* Phones: each button's 128px min-width made the pair (+ gap) wider than the
   card at 320px, so they wrapped into a stack. Share the row evenly with a
   slightly smaller face instead. */
@media (max-width: 575.98px) {
  .timesheet-toolbar-actions,
  .view-switch {
    width: 100%;
  }

  .view-switch {
    flex-wrap: nowrap;

    /deep/ .btn {
      flex: 1 1 0;
      min-width: 0;
      padding: 0.5rem 0.4rem;
      font-size: 12.5px;
      white-space: nowrap;
    }
  }
}

.week-start-display {
  min-height: 34px;
  display: flex;
  align-items: center;
  max-width: 220px;
  padding: 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #f8fafc;
  font-size: 13px;
  color: #1f2d3d;
}

.week-start-hint {
  margin-top: 4px;
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
}

.submit-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.submit-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  overflow: hidden;
}

.submit-card-header {
  padding: 16px 18px;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}

.submit-card-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.submit-card-subtitle {
  margin: 4px 0 0;
  font-size: 13px;
  color: #6b7280;
}

.submit-card /deep/ > form,
.submit-card /deep/ > div:not(.submit-card-header) {
  padding: 18px;
}
</style>
